import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Divider,
  TextField,
  Switch,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../context/AuthContext";
import { formatPhoneNumber } from "../../utils/functions";
import AvatarUpload from "../shared/AvatarUpload";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "15px",
      color: "#111008",
      fontFamily: "inherit",
      "::placeholder": { color: "#b0a898" },
    },
    invalid: { color: "#e53935" },
  },
};

// ── Add Card form (must be inside <Elements>) ─────────────────────────────────
const AddCardForm = ({ onAdded, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const api = useApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    setLoading(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      const { error: stripeError, paymentMethod: pm } =
        await stripe.createPaymentMethod({ type: "card", card: cardElement });

      if (stripeError) {
        setError(stripeError.message);
        return;
      }

      await api("/stripe/payment-methods.php", {
        method: "POST",
        body: JSON.stringify({ payment_method_id: pm.id }),
      });

      onAdded();
    } catch (e) {
      setError(e.message || "Failed to save card. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
        p: 2.5,
        borderRadius: 2,
        border: "1px solid #e8e2d8",
        backgroundColor: "#fafaf8",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          mb: 1.5,
          color: "text.secondary",
          fontSize: 11,
          letterSpacing: 0.5,
          textTransform: "uppercase",
        }}
      >
        New card
      </Typography>

      <Box
        sx={{
          border: "1px solid #d8d1c7",
          borderRadius: "10px",
          p: 2,
          backgroundColor: "white",
          mb: 1.5,
        }}
      >
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 1.5, fontSize: 12, borderRadius: 1.5 }}
        >
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 1 }}>
        <Button
          onClick={onCancel}
          fullWidth
          sx={{
            textTransform: "none",
            color: "text.secondary",
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || !stripe}
          fullWidth
          sx={{
            backgroundColor: "#111008",
            color: "white",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 1.5,
            "&:hover": { backgroundColor: "#1e1c14" },
            "&:disabled": {
              backgroundColor: "rgba(0,0,0,0.15)",
              color: "rgba(0,0,0,0.4)",
            },
          }}
        >
          {loading ? (
            <CircularProgress size={16} sx={{ color: "white" }} />
          ) : (
            "Save card"
          )}
        </Button>
      </Box>
    </Box>
  );
};

// ── Change password section ───────────────────────────────────────────────────
const ChangePasswordSection = ({ api }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  const handleSubmit = async () => {
    setAlert(null);
    if (newPassword.length < 8) {
      setAlert({
        type: "error",
        message: "New password must be at least 8 characters.",
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      setAlert({ type: "error", message: "Passwords do not match." });
      return;
    }
    setLoading(true);
    try {
      await api("/auth/change-password.php", {
        method: "POST",
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }),
      });
      setAlert({ type: "success", message: "Password updated successfully." });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      setAlert({
        type: "error",
        message: e.message || "Failed to update password.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="overline" sx={{ display: "block", mb: 1.5 }}>
        Change Password
      </Typography>
      <Box
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "customDivider",
          backgroundColor: "offWhite",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {alert && (
          <Alert
            severity={alert.type}
            onClose={() => setAlert(null)}
            sx={{ fontSize: 12, borderRadius: 1.5 }}
          >
            {alert.message}
          </Alert>
        )}
        <TextField
          type="password"
          size="small"
          placeholder="Current password"
          value={currentPassword}
          disabled={loading}
          onChange={(e) => setCurrentPassword(e.target.value)}
        />
        <TextField
          type="password"
          size="small"
          placeholder="New password"
          value={newPassword}
          disabled={loading}
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <TextField
          type="password"
          size="small"
          placeholder="Confirm new password"
          value={confirmPassword}
          disabled={loading}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            onClick={handleSubmit}
            disabled={
              loading || !currentPassword || !newPassword || !confirmPassword
            }
            sx={{
              backgroundColor: "#111008",
              color: "white",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              borderRadius: 1.5,
              px: 2,
              "&:hover": { backgroundColor: "#1e1c14" },
              "&.Mui-disabled": {
                backgroundColor: "rgba(0,0,0,0.15)",
                color: "rgba(0,0,0,0.4)",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={16} sx={{ color: "white" }} />
            ) : (
              "Update Password"
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

// ── Notifications section ─────────────────────────────────────────────────────
const NotificationsSection = ({ api }) => {
  const [prefs, setPrefs] = useState(null); // member prefs (email/sms/push)
  // Shop-level switches: a false channel here means the shop disabled it, so the
  // client's row for it renders disabled with a note.
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);

  const fetchPrefs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api("/tenants/notification-preferences.php", {
        method: "GET",
      });
      setPrefs(res.data.preferences);
      setShop(res.data.shop ?? null);
    } catch {
      setAlert({
        type: "error",
        message: "Could not load notification settings.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrefs();
  }, []);

  const handleToggle = async (key, value) => {
    if (!prefs) return;
    const previous = prefs;
    const next = { ...prefs, [key]: value };
    setPrefs(next); // optimistic
    setSaving(true);
    setAlert(null);
    try {
      await api("/tenants/notification-preferences.php", {
        method: "PUT",
        body: JSON.stringify({
          email_notifications_enabled: next.email_notifications_enabled,
          sms_notifications_enabled: next.sms_notifications_enabled,
          push_notifications_enabled: next.push_notifications_enabled,
        }),
      });
    } catch (e) {
      setPrefs(previous); // revert on failure
      setAlert({
        type: "error",
        message: e.message || "Failed to update notification settings.",
      });
    } finally {
      setSaving(false);
    }
  };

  const rows = [
    {
      key: "email_notifications_enabled",
      label: "Email notifications",
      help: "Booking confirmations, reschedules, and cancellations by email.",
    },
    {
      key: "sms_notifications_enabled",
      label: "Text messages (SMS)",
      help: "Booking confirmations, reschedules, and cancellations by text message.",
    },
    {
      key: "push_notifications_enabled",
      label: "Push notifications",
      help: "Booking confirmations, reschedules, cancellations, and reminders on the mobile app.",
    },
  ];

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="overline" sx={{ display: "block", mb: 1.5 }}>
        Notifications
      </Typography>
      <Box
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "customDivider",
          backgroundColor: "offWhite",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {alert && (
          <Alert
            severity={alert.type}
            onClose={() => setAlert(null)}
            sx={{ fontSize: 12, borderRadius: 1.5 }}
          >
            {alert.message}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
            <CircularProgress size={16} sx={{ color: "text.secondary" }} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Loading...
            </Typography>
          </Box>
        ) : (
          prefs &&
          rows.map(({ key, label, help }) => {
            const shopOff = shop && shop[key] === false;
            return (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: shopOff ? "text.disabled" : "text.primary" }}
                  >
                    {label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {shopOff ? "Turned off by the shop." : help}
                  </Typography>
                </Box>
                <Switch
                  // Shop disabled this channel → force off and lock it; a member
                  // can't opt into a channel the shop killed.
                  checked={!shopOff && !!prefs[key]}
                  disabled={saving || shopOff}
                  onChange={(e) => handleToggle(key, e.target.checked)}
                />
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

// ── Main profile component ────────────────────────────────────────────────────
const ClientProfile = () => {
  const api = useApi();
  const { user, setUser } = useAuth();

  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);
  const [removeError, setRemoveError] = useState(null);
  const [showAddCard, setShowAddCard] = useState(false);

  const fetchCards = useCallback(async () => {
    setCardsLoading(true);
    try {
      const res = await api("/stripe/payment-methods.php", { method: "GET" });
      setCards(res.data.payment_methods || []);
    } catch {
      setCards([]);
    } finally {
      setCardsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCards();
  }, []);

  const handleRemove = async (paymentMethodId) => {
    setRemovingId(paymentMethodId);
    setRemoveError(null);
    try {
      await api("/stripe/payment-methods.php", {
        method: "DELETE",
        body: JSON.stringify({ payment_method_id: paymentMethodId }),
      });
      setCards((prev) => prev.filter((c) => c.id !== paymentMethodId));
    } catch (e) {
      setRemoveError(e.message || "Failed to remove card.");
    } finally {
      setRemovingId(null);
    }
  };

  const handleAdded = () => {
    setShowAddCard(false);
    fetchCards();
  };

  return (
    <Box sx={{ p: { xs: 3, md: 5 }, maxWidth: 640 }}>
      {/* Header */}
      <Typography variant="h3" sx={{ mb: 0.5 }}>
        Profile
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
        Manage your account details and payment methods.
      </Typography>

      {/* Avatar */}
      <AvatarUpload user={user} api={api} setUser={setUser} />

      {/* Account info */}
      <Typography variant="overline" sx={{ display: "block", mb: 1.5 }}>
        Account
      </Typography>
      <Box
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "customDivider",
          backgroundColor: "offWhite",
          mb: 4,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {[
          {
            label: "Name",
            value: `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim(),
          },
          { label: "Email", value: user?.email ?? "—" },
          {
            label: "Phone",
            value: user?.phone_number
              ? formatPhoneNumber(user.phone_number)
              : "—",
          },
          {
            label: "Role",
            value: user?.role
              ? user.role.charAt(0).toUpperCase() + user.role.slice(1)
              : "—",
          },
        ].map(({ label, value }) => (
          <Box
            key={label}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", minWidth: 80 }}
            >
              {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Notifications */}
      <NotificationsSection api={api} />

      <Divider sx={{ mb: 4 }} />

      {/* Change password */}
      <ChangePasswordSection api={api} />

      <Divider sx={{ mb: 4 }} />

      {/* Saved cards */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Typography variant="overline">Saved Cards</Typography>
        {!showAddCard && (
          <Button
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={() => setShowAddCard(true)}
            size="small"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              color: "#111008",
              borderRadius: 1.5,
              border: "1px solid #e0e0e0",
              px: 1.5,
              "&:hover": { backgroundColor: "#f5f5f5" },
            }}
          >
            Add card
          </Button>
        )}
      </Box>

      <Typography
        variant="body2"
        sx={{ color: "text.secondary", mb: 2, fontSize: 12 }}
      >
        Cards saved here are used for no-show and late cancellation fees. Your
        card is never charged without notice.
      </Typography>

      {removeError && (
        <Alert
          severity="error"
          onClose={() => setRemoveError(null)}
          sx={{ mb: 2, fontSize: 12, borderRadius: 1.5 }}
        >
          {removeError}
        </Alert>
      )}

      {cardsLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
          <CircularProgress size={16} sx={{ color: "text.secondary" }} />
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Loading saved cards...
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {cards.length === 0 && !showAddCard && (
            <Box
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "customDivider",
                backgroundColor: "offWhite",
              }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                No saved cards. Add one to enable automatic fee protection.
              </Typography>
            </Box>
          )}

          {cards.map((card) => {
            const isLastCard = cards.length === 1;
            const isRemoving = removingId === card.id;
            return (
              <Box
                key={card.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "customDivider",
                  backgroundColor: "offWhite",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <CreditCardIcon
                    sx={{ fontSize: 20, color: "text.secondary" }}
                  />
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, textTransform: "capitalize" }}
                    >
                      {card.brand} •••• {card.last4}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      Expires {card.exp_month}/{String(card.exp_year).slice(-2)}
                    </Typography>
                    {isLastCard && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          display: "block",
                          mt: 0.25,
                        }}
                      >
                        Add a new card before removing this one.
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Button
                  onClick={() => handleRemove(card.id)}
                  disabled={!!removingId || isLastCard}
                  size="small"
                  startIcon={
                    isRemoving ? (
                      <CircularProgress size={12} />
                    ) : (
                      <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                    )
                  }
                  sx={{
                    textTransform: "none",
                    fontSize: 12,
                    color: "#A32D2D",
                    fontWeight: 500,
                    borderRadius: 1.5,
                    border: "1px solid #e0e0e0",
                    px: 1.5,
                    "&:hover": {
                      backgroundColor: "#fdf6f6",
                      borderColor: "#A32D2D",
                    },
                  }}
                >
                  Remove
                </Button>
              </Box>
            );
          })}

          {showAddCard && (
            <Elements stripe={stripePromise}>
              <AddCardForm
                onAdded={handleAdded}
                onCancel={() => setShowAddCard(false)}
              />
            </Elements>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ClientProfile;
